export type OnboardingPrefillTarget =
  | 'store'
  | 'owner_user'
  | 'secondary_profile'
  | 'tfv'
  | 'all';

export type PhoneOrderHandling =
  | 'agent_takes_order'
  | 'send_order_link'
  | 'transfer_to_store'
  | 'do_not_accept';

export type DeliveryHandling =
  | 'agent_takes_delivery'
  | 'explain_third_party_delivery'
  | 'pickup_only';

export type PaymentHandling = 'online_only' | 'pay_on_site' | 'both';

/** `transfer` is no longer offered (the agent has no path for it); kept for older intakes. */
export type ReservationHandling =
  | 'book_directly'
  | 'send_link'
  | 'transfer'
  | 'decline';

/** `grace_period_only` is no longer offered; kept for older intakes. */
export type LateReservationHandling =
  | 'grace_period_only'
  | 'notify_staff'
  | 'transfer';

export type ReservationCancellationFailureHandling =
  | 'transfer'
  | 'notify_staff';

/** `basic_info_only` is no longer offered; kept for older intakes. */
export type EventHandling =
  | 'text_link'
  | 'transfer'
  | 'collect_lead'
  | 'basic_info_only'
  | 'decline';

export type CateringHandling =
  | 'transfer'
  | 'provide_contact'
  | 'collect_lead'
  | 'decline';

/** `explain_only` is no longer offered (the agent can only text a link); kept for older intakes. */
export type GiftCardHandling = 'send_link' | 'explain_only' | 'unavailable';

export type POSSystem =
  | 'UNKNOWN'
  | 'TOAST'
  | 'FOODHUB'
  | 'CLOVER'
  | 'SQUARE'
  | 'OTHER';

export interface OnboardingOrderingSection {
  pos_system?: POSSystem | string;
  current_pos_system?: POSSystem;
  pos_other_name?: string;

  phone_order_handling?: PhoneOrderHandling;
  delivery_handling?: DeliveryHandling;
  payment_handling?: PaymentHandling;

  ordering_link?: string;
  menu_link?: string;

  order_requests_phone_number?: string;
  order_requests_phone_number_ext?: string;
  how_to_order_delivery_message?: string;

  last_order_offset_minutes?: number;

  average_lunch_prep_time_minutes?: number;
  average_dinner_prep_time_minutes?: number;
  default_prep_time_minutes?: number;

  // Legacy fields preserved for older intakes.
  menu_url?: string;
  ordering_url?: string;
  average_prep_time_lunch_minutes?: number | null;
  average_prep_time_dinner_minutes?: number | null;
  dynamic_peak_prep_times?: boolean | null;
  payment_preference?: string;
  upsell_aggressiveness?: string;
  upsell_items?: string[];
  default_double_burger?: string;
  drinks_shakes_trigger?: string;
  always_push_items?: string;
  brand_voice?: string;

  [key: string]: unknown;
}

export interface OnboardingReservationsSection {
  handling?: ReservationHandling;
  reservation_link?: string;
  reservation_email?: string;
  max_party_size?: number;
  default_grace_period_minutes?: number;
  late_handling?: LateReservationHandling;
  cancellation_failure_handling?: ReservationCancellationFailureHandling;
  transfer_phone_number?: string;
  special_requests_allowed?: boolean;
  seating_preferences_supported?: boolean;
}

export interface OnboardingEventsSection {
  handling?: EventHandling;
  event_forwarding_number?: string;
  event_forwarding_number_ext?: string;
  event_link?: string;
  event_inquiry_questions?: string[];
  event_inquiry_prompt?: string;
}

export interface OnboardingCateringSection {
  handling?: CateringHandling;
  catering_forwarding_number?: string;
  catering_forwarding_number_ext?: string;
  catering_outreach_contact?: string;
  catering_link?: string;
}

export interface OnboardingGiftCardsSection {
  handling?: GiftCardHandling;
  gift_card_link?: string;
  gift_card_instructions?: string;
}

export interface OnboardingStoreFaqsSection {
  parking_info?: string;
  outdoor_seating?: boolean;
  accessibility_info?: string;
  allergen_policy?: string;
  gluten_free_policy?: string;
  vegan_vegetarian_policy?: string;
  kids_menu?: string;
  corkage_policy?: string;
  dress_code?: string;
  pet_policy?: string;
  wifi?: string;
  delivery_platforms?: string;
  refund_issue_policy?: string;
  lost_items_policy?: string;
}

export interface OnboardingPayload {
  business: {
    restaurant_name: string;
    legal_business_name: string;
    doing_business_as: string;
    website: string;
    store_group_name: string;
    setup_type: string;
    services_used: string[];
    biggest_pain_point: string;
  };
  location: {
    legal_business_address_raw: string;
    legal_address_line_1: string;
    legal_address_line_2: string;
    legal_city: string;
    legal_state: string;
    legal_postal_code: string;
    legal_country: string;
    restaurant_address_raw: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    timezone: string;
    phone: string;
  };
  primary_contact: {
    name: string;
    title_role: string;
    phone: string;
    email: string;
  };
  authorized_representative: {
    name: string;
    phone: string;
    email: string;
    business_title: string;
    job_position: string;
  };
  registration: {
    tax_id: string;
    canadian_business_number: string;
    type: string;
    authority: string;
    country: string;
    issuing_country: string;
    business_type: string;
  };
  call_handling: {
    arthur_should_handle: string[];
    events_catering_behavior: string;
    event_catering_forwarding: string;
    additional_notes: string;
    /** Yes transfers to a person; no means text or email staff instead. Unset until answered. */
    human_transfer_enabled?: boolean;
    /** Where general transfers go. Optional at intake; the onboarding checklist flags it if missing. */
    transfer_phone_number?: string;
    transfer_phone_number_ext?: string;
  };
  ordering: OnboardingOrderingSection;
  reservations: OnboardingReservationsSection;
  events: OnboardingEventsSection;
  catering: OnboardingCateringSection;
  gift_cards: OnboardingGiftCardsSection;
  store_faqs: OnboardingStoreFaqsSection;
  knowledge_base: {
    frequent_topics: string[];
    specific_faqs: string;
    faq_url: string;
  };
  assets: {
    docs_email_status: string;
  };
  notes: string;
}

export interface OnboardingIntakeFile {
  id: number;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
}

/** A shared intake link (group onboarding); see `features/groupOnboarding`. */
export interface OnboardingIntakeLink {
  state: 'open' | 'submitted' | 'approved' | 'revoked' | 'expired';
  url: string;
  location_name: string;
  store_group_id?: number | null;
  store_group_name: string;
  copied_from_name: string;
  confirmed: number;
  total: number;
}

export interface OnboardingIntake {
  id: number;
  link?: OnboardingIntakeLink | null;
  payload?: Partial<OnboardingPayload>;
  files?: OnboardingIntakeFile[];
  status?: string;
  store?: number | { id?: number; name?: string } | null;
  store_id?: number | null;
  store_name?: string | null;
  linked_store?: number | { id?: number; name?: string } | null;
  linked_store_id?: number | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface OnboardingPrefillResponse<T = Record<string, unknown>> {
  payload?: Partial<T>;
  [key: string]: unknown;
}

/**
 * Questions stored for a store that has not written its own list.
 * The name-and-contact question is always asked and is not part of this list.
 * Keep in step with DEFAULT_EVENT_INQUIRY_QUESTIONS and
 * DEFAULT_CATERING_INQUIRY_QUESTIONS in event_inquiries/views.py.
 */
export const DEFAULT_EVENT_INQUIRY_QUESTIONS = [
  'When are you thinking of having this event?',
  'How many people are you expecting?',
  'Can you describe the event you want in detail?',
];

export const DEFAULT_CATERING_INQUIRY_QUESTIONS = [
  'When do you need the catering?',
  'How many people are you expecting?',
  "Can you describe what you'd like to order?",
];

export const createEmptyOnboardingPayload = (): OnboardingPayload => ({
  business: {
    restaurant_name: '',
    legal_business_name: '',
    doing_business_as: '',
    website: '',
    store_group_name: '',
    setup_type: 'Single location',
    services_used: [],
    biggest_pain_point: '',
  },
  location: {
    legal_business_address_raw: '',
    legal_address_line_1: '',
    legal_address_line_2: '',
    legal_city: '',
    legal_state: '',
    legal_postal_code: '',
    legal_country: 'US',
    restaurant_address_raw: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    timezone: '',
    phone: '',
  },
  primary_contact: {
    name: '',
    title_role: '',
    phone: '',
    email: '',
  },
  authorized_representative: {
    name: '',
    phone: '',
    email: '',
    business_title: '',
    job_position: '',
  },
  registration: {
    tax_id: '',
    canadian_business_number: '',
    type: '',
    authority: '',
    country: '',
    issuing_country: '',
    business_type: '',
  },
  call_handling: {
    arthur_should_handle: [],
    events_catering_behavior: '',
    event_catering_forwarding: '',
    additional_notes: '',
  },
  ordering: {
    pos_system: '',
    menu_url: '',
    ordering_url: '',
    average_prep_time_lunch_minutes: null,
    average_prep_time_dinner_minutes: null,
    dynamic_peak_prep_times: null,
    payment_preference: '',
    upsell_aggressiveness: '',
    upsell_items: [],
    default_double_burger: '',
    drinks_shakes_trigger: '',
    always_push_items: '',
    brand_voice: '',
  },
  reservations: {},
  events: {
    handling: 'decline',
    event_inquiry_questions: [...DEFAULT_EVENT_INQUIRY_QUESTIONS],
  },
  catering: {
    handling: 'decline',
  },
  gift_cards: {},
  store_faqs: {},
  knowledge_base: {
    frequent_topics: [],
    specific_faqs: '',
    faq_url: '',
  },
  assets: {
    docs_email_status: '',
  },
  notes: '',
});

export const splitName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' '),
  };
};

export const isBlankValue = (value: unknown) =>
  value === '' ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

export function mergePrefill<T extends Record<string, any>>(
  current: T,
  incoming: Partial<T>,
  overwrite: boolean,
): T {
  return Object.entries(incoming).reduce(
    (next, [key, value]) => {
      if (value === undefined) return next;
      if (overwrite || isBlankValue(next[key])) {
        return { ...next, [key]: value };
      }
      return next;
    },
    { ...current },
  );
}

/**
 * These switches have a form default, which is not "blank", so a normal
 * prefill would keep that default. Takes Reservations stays off, and Accepts
 * Phone Orders stays on, even when the intake answered the other way.
 * An explicit boolean from the intake fills that switch either way.
 */
const EXPLICIT_STORE_INTAKE_BOOLEANS = [
  'human_transfer_enabled',
  'accepts_phone_orders',
  'forward_order_requests_to_text_link',
  'forward_order_requests_to_phone',
  'accepts_deliveries',
  'collect_payment_online',
  'accept_pay_on_site',
  'takes_reservations',
  'forward_late_reservations_to_text',
  'forward_reservation_cancelation_failures_to_text',
  'takes_events',
  'agent_handles_events',
  'forward_event_requests_to_store',
  'forward_event_requests_to_text_link',
  'takes_catering',
  'agent_handles_catering',
  'forward_catering_requests_to_store',
  'forward_catering_requests_to_text_link',
  'outdoor_seating',
];

export function mergeStoreIntakePrefill<T extends Record<string, any>>(
  current: T,
  incoming: Partial<T>,
  overwrite: boolean,
): T {
  const base = { ...current };
  if (!overwrite) {
    for (const key of EXPLICIT_STORE_INTAKE_BOOLEANS) {
      if (typeof incoming[key] === 'boolean') {
        (base as Record<string, unknown>)[key] = undefined;
      }
    }
  }
  return mergePrefill(base, incoming, overwrite);
}

export const getIntakeStoreId = (intake: OnboardingIntake) => {
  const linked = intake.linked_store;
  if (typeof linked === 'number') return linked;
  if (linked && typeof linked === 'object' && linked.id) return linked.id;
  if (typeof intake.linked_store_id === 'number') return intake.linked_store_id;
  const store = intake.store;
  if (typeof store === 'number') return store;
  if (store && typeof store === 'object' && store.id) return store.id;
  return typeof intake.store_id === 'number' ? intake.store_id : null;
};

export const getIntakeStoreName = (intake: OnboardingIntake) => {
  const linked = intake.linked_store;
  if (linked && typeof linked === 'object' && linked.name) return linked.name;
  const store = intake.store;
  if (store && typeof store === 'object' && store.name) return store.name;
  return intake.store_name || '';
};

export const getIntakeRestaurantName = (intake: OnboardingIntake) =>
  intake.payload?.business?.restaurant_name ||
  String(intake.restaurant_name || intake.name || '');

export const getIntakeLegalName = (intake: OnboardingIntake) =>
  intake.payload?.business?.legal_business_name ||
  String(intake.legal_business_name || '');

export const getIntakePrimaryContact = (intake: OnboardingIntake) => ({
  name:
    intake.payload?.primary_contact?.name ||
    String(intake.primary_contact_name || ''),
  email:
    intake.payload?.primary_contact?.email ||
    String(intake.primary_contact_email || ''),
  phone:
    intake.payload?.primary_contact?.phone ||
    String(intake.primary_contact_phone || ''),
});
